import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile, useIsTouchDevice } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const DEFAULT_CURRENT_USER_FILTER_APPLY_DELAY_MS = 100;

type UseCurrentUserFilterToggleOptions = Omittable<{
  delayMs?: number;
  initialSelected?: boolean;
}>;

export function useCurrentUserFilterToggle(
  options: UseCurrentUserFilterToggleOptions = {},
) {
  const {
    delayMs = DEFAULT_CURRENT_USER_FILTER_APPLY_DELAY_MS,
    initialSelected = false,
  } = options;
  const [isCurrentUserButtonSelected, setIsCurrentUserButtonSelected] =
    useState(initialSelected);
  const [isCurrentUserFilterApplied, setIsCurrentUserFilterApplied] =
    useState(initialSelected);
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = useIsMobile();
  // This is NOT a hover-reveal gate (those are CSS `pointer-coarse:`
  // variants) — press SEMANTICS differ:
  // desktop always resolves to "on" because the clear affordance is a
  // separate control, while touch has to toggle.
  const isTouchDevice = useIsTouchDevice();
  const isDesktop = !isMobile && !isTouchDevice;

  const syncCurrentUserFilter = useCallback(
    (nextSelected: boolean) => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
      filterTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setIsCurrentUserFilterApplied(nextSelected);
        });
      }, delayMs);
    },
    [delayMs],
  );

  const handleCurrentUserPress = useCallback(() => {
    setIsCurrentUserButtonSelected((value) => {
      const nextSelected = isDesktop ? true : !value;
      syncCurrentUserFilter(nextSelected);
      return nextSelected;
    });
  }, [isDesktop, syncCurrentUserFilter]);

  const handleCurrentUserClear = useCallback(() => {
    setIsCurrentUserButtonSelected(false);
    syncCurrentUserFilter(false);
  }, [syncCurrentUserFilter]);

  useEffect(() => () => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
  }, []);

  return {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    // Exposed so callers can derive the value a press will resolve to
    // (`isDesktop ? true : !selected`) — e.g. for analytics that must
    // report the actual on/off the toggle landed on, not a hardcoded guess.
    isDesktop,
    handleCurrentUserPress,
    handleCurrentUserClear,
  };
}
